import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import InfoIcon from "@mui/icons-material/Info";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 视频容器 - 移动端优化
const VideoContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  paddingBottom: "56.25%", // 16:9
  height: 0,
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  backgroundColor: "#000",
  marginTop: theme.spacing(1.5),
  "& iframe": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    border: "none",
  },
}));

// 事奉内容卡片
const MinistryCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: "10px",
  background: theme.palette.mode === "dark"
    ? "rgba(28, 28, 30, 0.4)"
    : "rgba(255, 255, 255, 0.6)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: theme.palette.mode === "dark"
    ? "0.5px solid rgba(84, 84, 88, 0.5)"
    : "0.5px solid rgba(0, 0, 0, 0.08)",
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  "&:active": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(28, 28, 30, 0.6)"
      : "rgba(255, 255, 255, 0.8)",
  },
}));

// 获取 YouTube 视频 ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

const MobileServeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchWorkerDetails = async () => {
      if (!id) {
        setWorker(null);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/ministry-worker/${id}`
        );
        setWorker(response.data);
      } catch (err) {
        console.error("获取同工详情失败:", err);
        setError("获取同工详情失败，请稍后再试");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkerDetails();
  }, [id]);

  const getImageUrl = (path) => {
    if (!path) return `${API_BASE_URL}/images/avatars/default-avatar.png`;
    return `${API_BASE_URL}${path}`;
  };

  const handleMinistryClick = (ministry) => {
    navigate(`/vision/${ministry._id}`);
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  if (loading) {
    return (
      <Box sx={{ px: 2, py: 2.5, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={24} sx={{ color: textColor }} />
      </Box>
    );
  }

  if (error || !worker) {
    return (
      <Box sx={{ px: 2, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
            返回
          </Typography>
        </Box>
        <Typography sx={{ color: secondaryTextColor, fontSize: "0.85rem", textAlign: "center", py: 4 }}>
          {error || "未找到同工详情"}
        </Typography>
      </Box>
    );
  }

  const videoId = worker.videoUrl ? getYouTubeVideoId(worker.videoUrl) : null;

  return (
    <Box sx={{ px: 2, py: 2.5, pb: 4 }}>
      {/* 返回按钮和标题 */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: textColor, mr: 1, padding: 0.5 }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 600, color: textColor }}>
          {worker.name}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 头像 */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
        <Avatar
          src={
            imageError
              ? `${API_BASE_URL}/images/avatars/default-avatar.png`
              : getImageUrl(worker.avatar)
          }
          alt={worker.name}
          onError={() => setImageError(true)}
          sx={{
            width: 120,
            height: 120,
          }}
        />
      </Box>

      {/* 职位 */}
      {worker.position && (
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: secondaryTextColor,
            textAlign: "center",
            mb: 2,
          }}
        >
          {worker.position}
        </Typography>
      )}

      {/* 介绍 */}
      {worker.introduction && (
        <Typography
          sx={{
            fontSize: "0.85rem",
            color: textColor,
            lineHeight: 1.6,
            whiteSpace: "pre-line",
            mb: 2,
          }}
        >
          {worker.introduction}
        </Typography>
      )}

      <Divider sx={{ borderColor: borderColor, mb: 2 }} />

      {/* 联系方式 */}
      {(worker.email || worker.phone) && (
        <Box sx={{ mb: 2 }}>
          {worker.email && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <EmailIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
              <Typography sx={{ fontSize: "0.8rem", color: textColor }}>
                {worker.email}
              </Typography>
            </Box>
          )}
          {worker.phone && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
              <Typography sx={{ fontSize: "0.8rem", color: textColor }}>
                {worker.phone}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* 事奉内容 */}
      {worker.ministries && worker.ministries.length > 0 ? (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <InfoIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
            <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
              事奉内容
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            {worker.ministries.map((ministry) => (
              <Grid item xs={6} key={ministry._id}>
                <MinistryCard onClick={() => handleMinistryClick(ministry)}>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      color: textColor,
                      mb: 0.5,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {ministry.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: secondaryTextColor,
                      mb: 0.75,
                    }}
                  >
                    {ministry.date ? new Date(ministry.date).toLocaleDateString() : ""}
                  </Typography>
                  <Chip
                    label={ministry.status === "upcoming" ? "即将开始" : "已完成"}
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      height: "20px",
                      backgroundColor: prefersDarkMode
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                      color: textColor,
                      border: `0.5px solid ${borderColor}`,
                    }}
                  />
                </MinistryCard>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <InfoIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
            <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
              事奉内容
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.8rem", color: secondaryTextColor, fontStyle: "italic" }}>
            暂无事奉内容
          </Typography>
        </Box>
      )}

      {/* 视频 */}
      {videoId && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <PlayCircleOutlineIcon sx={{ fontSize: "0.85rem", color: secondaryTextColor }} />
            <Typography sx={{ fontSize: "0.75rem", color: secondaryTextColor }}>
              自我介绍小视频
            </Typography>
          </Box>
          <VideoContainer>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0&modestbranding=1`}
              title="自我介绍小视频"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              playsInline
            />
          </VideoContainer>
        </Box>
      )}
    </Box>
  );
};

export default MobileServeDetail;

